import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';
import CodeSnippet from '../components/CodeSnippet';
import Modal from '../components/UI/Modal';
import Input from '../components/UI/Input';
import { API_LIMITS } from '../utils/constants';
import { authAPI } from '../services/api';
import axios from 'axios';
import toast from 'react-hot-toast';

const Profile = () => {
  const { isAuthenticated, logout, user, userRoles } = useAuth();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [token, setToken] = useState('');
  const [tokenRevealed, setTokenRevealed] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    const loadProfile = async () => {
      try {
        const response = await authAPI.getCurrentUser();
        setUserDetails(response.data);
        const storedToken = localStorage.getItem('access_token');
        if (storedToken) {
          setToken(maskToken(storedToken));
        }
      } catch (error) {
        if (error.response?.status === 401) {
          logout();
          navigate('/login');
        } else {
          toast.error('Не вдалося завантажити профіль');
        }
      }
    };
    loadProfile();
  }, [isAuthenticated, navigate, logout]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setTimeout(() => setCooldownRemaining(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownRemaining]);

  const startCooldown = () => setCooldownRemaining(60);

  const maskToken = (fullToken) => {
    if (!fullToken) return '';
    return fullToken.slice(0, 10) + '••••••••••••••••' + fullToken.slice(-10);
  };

  const revealToken = () => {
    const fullToken = localStorage.getItem('access_token');
    if (fullToken) {
      setToken(fullToken);
      setTokenRevealed(true);
    }
  };

  const hideToken = () => {
    const fullToken = localStorage.getItem('access_token');
    if (fullToken) {
      setToken(maskToken(fullToken));
      setTokenRevealed(false);
    }
  };

  const toggleTokenVisibility = () => {
    if (tokenRevealed) hideToken();
    else revealToken();
  };

  const copyTokenToClipboard = () => {
    const fullToken = localStorage.getItem('access_token');
    if (fullToken) {
      navigator.clipboard.writeText(fullToken);
      toast.success('Токен скопійовано');
    } else {
      toast.error('Токен не знайдено');
    }
  };

  const tryRefreshToken = async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) throw new Error('No refresh token');
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const { access_token, refresh_token: newRefreshToken } = response.data;
    localStorage.setItem('access_token', access_token);
    if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
    return { access_token, refresh_token: newRefreshToken };
  };

  const reauthenticateWithPassword = async (password) => {
    const username = userDetails?.username || user?.username || user?.email;
    if (!username) throw new Error('Не вдалося визначити імʼя користувача');
    const response = await authAPI.login(username, password);
    const { access_token, refresh_token: newRefreshToken } = response.data;
    localStorage.setItem('access_token', access_token);
    if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
    return { access_token, refresh_token: newRefreshToken };
  };

  const handleRegenerateToken = async () => {
    setLoadingToken(true);
    try {
      await tryRefreshToken();
      const newToken = localStorage.getItem('access_token');
      setToken(maskToken(newToken));
      setTokenRevealed(false);
      toast.success('Токен оновлено');
      startCooldown();
    } catch (refreshError) {
      setShowPasswordModal(true);
    } finally {
      setLoadingToken(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setPasswordError('Введіть пароль');
      return;
    }
    setSubmitting(true);
    setPasswordError('');
    try {
      await reauthenticateWithPassword(password);
      const newToken = localStorage.getItem('access_token');
      setToken(maskToken(newToken));
      setTokenRevealed(false);
      toast.success('Токен оновлено');
      startCooldown();
      setShowPasswordModal(false);
      setPassword('');
    } catch (err) {
      toast.error('Невірний пароль');
      logout();
      navigate('/login');
    } finally {
      setSubmitting(false);
    }
  };

  const apiExample = `curl -X 'GET' \\
  'https://govpoll-api.votes.holka.org/api/v1/lakehouse/deputies?page=1&limit=50&organ_id=1&sort_by=id&order=desc' \\
  -H 'accept: application/json' \\
  -H 'Authorization: Bearer ${tokenRevealed ? localStorage.getItem('access_token') : 'YOUR_API_TOKEN'}'`;

  const isButtonDisabled = loadingToken || cooldownRemaining > 0;
  const buttonText = loadingToken
    ? 'Оновлення...'
    : cooldownRemaining > 0
      ? `Зачекайте ${cooldownRemaining}с`
      : 'Отримати новий токен';

  if (!isAuthenticated) return null;

  return (
    <div className="py-8">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Профіль користувача</h1>
          <p className="text-gray-600 mt-2">Ваші персональні дані та API токен</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column – only email (small, compact) */}
          <div className="lg:col-span-1 space-y-6">
            <Card title="Інформація профілю" subtitle="Дані облікового запису">
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-500 uppercase font-semibold">Email</label>
                  <p className="text-gray-900 text-sm mt-1 break-all">
                    {userDetails?.email || user?.email || '—'}
                  </p>
                </div>
              </div>
            </Card>

            <Card title="Ліміти API" subtitle="Поточні обмеження вашого токена">
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-gray-600">Частота запитів</span><span className="text-sm font-mono font-semibold">{API_LIMITS.rateLimit}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Термін дії токена</span><span className="text-sm font-mono font-semibold">{API_LIMITS.tokenLifetime}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Ліміт пагінації</span><span className="text-sm font-mono font-semibold">{API_LIMITS.paginationLimit}</span></div>
              </div>
            </Card>
          </div>

          {/* Right Column - API Token */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="API токен" subtitle="Отримайте та керуйте вашим токеном доступу">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <label className="text-xs text-gray-500 uppercase font-semibold mb-2 block">Ваш токен доступу (JWT)</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={token}
                      readOnly
                      className="font-mono text-sm bg-white p-3 rounded border border-gray-200 w-full pr-24"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-2">
                      <button
                        onClick={toggleTokenVisibility}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title={tokenRevealed ? 'Приховати токен' : 'Показати токен'}
                      >
                        {tokenRevealed ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <Button onClick={copyTokenToClipboard} variant="secondary" size="sm">
                      📋 Скопіювати токен
                    </Button>
                    <Button onClick={handleRegenerateToken} variant="primary" size="sm" disabled={isButtonDisabled}>
                      {buttonText}
                    </Button>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-sm text-gray-700"><strong className="text-blue-700">Як це працює:</strong> спочатку система спробує оновити токен за допомогою refresh_token. Якщо refresh_token застарів або недійсний, зʼявиться вікно для повторного введення пароля. Після успішного оновлення кнопка стає недоступною на 60 секунд.</p>
                </div>
              </div>
            </Card>

            <Card title="Приклад використання" subtitle="Як використовувати токен у запитах до API">
              <div><CodeSnippet code={apiExample} /><div className="mt-4 text-sm text-gray-600"><p>Замініть <code className="bg-gray-100 px-1 py-0.5 rounded">YOUR_API_TOKEN</code> на ваш дійсний токен.</p><p className="mt-2">Параметри запиту: <code className="bg-gray-100 px-1 py-0.5 rounded">page</code>, <code className="bg-gray-100 px-1 py-0.5 rounded">limit</code> (≤200), <code className="bg-gray-100 px-1 py-0.5 rounded">organ_id</code>, <code className="bg-gray-100 px-1 py-0.5 rounded">sort_by</code>, <code className="bg-gray-100 px-1 py-0.5 rounded">order</code>.</p></div></div>
            </Card>

            <Card title="Швидкі посилання">
              <div className="space-y-2">
                <a href={process.env.REACT_APP_OPEN_API_DOC_URL || 'https://govpoll-api.votes.holka.org/docs'} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 block">
                  📘 Swagger UI (повна документація API)
                </a>
                <a href={process.env.REACT_APP_SUPERSET_APACHE_URL || 'https://superset.example.com'} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 block">
                  📊 Аналітика (Apache Superset)
                </a>
                {userRoles?.includes(process.env.REACT_APP_ADMIN_ROLE || 'admin') && (
                  <>
                    <a href={process.env.REACT_APP_ADMIN_PANEL_URL || '/admin'} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 block">
                      🛠️ Адмін панель
                    </a>
                    <Link to="/admin/users" className="text-purple-600 hover:text-purple-700 block">
                      👥 Керування користувачами
                    </Link>
                  </>
                )}
                <a href="/#features" className="text-blue-600 hover:text-blue-700 block">
                  ⚡ Можливості системи
                </a>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title="Підтвердження для отримання нового токена" size="sm">
        <form onSubmit={handlePasswordSubmit}>
          <div className="mb-4"><label className="block text-sm font-medium text-gray-700 mb-1">Ім'я користувача</label><input type="text" value={userDetails?.username || user?.username || user?.email || ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500" /></div>
          <Input label="Пароль" type="password" id="regenerate-password" value={password} onChange={(e) => setPassword(e.target.value)} error={passwordError} required placeholder="Введіть ваш пароль" />
          <div className="flex justify-end gap-3 mt-6"><Button type="button" variant="secondary" onClick={() => setShowPasswordModal(false)}>Скасувати</Button><Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Перевірка...' : 'Отримати токен'}</Button></div>
        </form>
      </Modal>
    </div>
  );
};

export default Profile;